import { AgGrid } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DocumentRefreshStatusEnum, RefreshStatusEnum } from "~/graphql/supergraphEnums";
import { capitalizeEachWord } from "~/utils/strings";
import { DomicileRenderer } from "../CaseDetailsDrawer/Profiles/DomicileRenderer";
import { ActiveCasesCellRenderer } from "../CompliancePositionsTable/ActiveCasesCellRenderer";
import { EscalationsCellRenderer } from "../CompliancePositionsTable/EscalationsCellRenderer";
import { AGGridCellRenderParams } from "../ComplianceTable/types";
import { sortStrings, mapStatusToText } from "../ComplianceTable/utils";
import { DocumentRefreshCellRenderer } from "./DocumentRefreshCellRenderer";
import { DOCUMENT_STATUS_CONFIG } from "./DocumentRefreshCellRenderer/config";
import {
  isDocumentExpiryStatus,
  mapDocumentRefreshToCaseStatus,
} from "./DocumentRefreshCellRenderer/utils";
import { PositionCellRenderer } from "./PositionCellRenderer";
import { ProfileNameRenderer } from "./ProfileNameRenderer";
import { RefreshStatusCellRenderer } from "./RefreshStatusCellRenderer";
import { STATUS_CONFIG } from "./RefreshStatusCellRenderer/config";
import { RefreshStatus } from "./RefreshStatusCellRenderer/types";
import { RoleCellRenderer } from "./RoleCellRenderer";
import { roleColumn, fillCell } from "./styles_vanilla.css";
import { ProfileRowItem } from "./types";

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  controller: "Controller",
  signatory: "Signatory",
  payment_beneficiary: "Payment Beneficiary",
  investment_profile: "Investment Profile",
};

const formatRoleValue = (value: string): string => {
  return ROLE_LABELS[value] || capitalizeEachWord(value.replace(/_/g, " "));
};

const formatStatusValue = <T extends string>(
  value: string,
  statusConfig: Partial<
    Record<T, { text: { content: string | { type: "dynamic"; render: unknown } } }>
  >,
  requestSentValue: T,
  requestSentOverdueValue: T,
): string => {
  const config = statusConfig[value as T];
  if (!config) return value;

  const content = config.text.content;
  if (typeof content === "string") {
    return content;
  }

  if (value === requestSentOverdueValue) {
    return "Request sent (overdue)";
  }
  if (value === requestSentValue) {
    return "Request sent";
  }

  return value;
};

const formatRefreshStatusValue = (value: string): string => {
  return formatStatusValue(
    value,
    STATUS_CONFIG,
    RefreshStatus.RequestSent,
    RefreshStatus.RequestSentOverdue,
  );
};

const formatDocumentRefreshStatusValue = (value: string): string => {
  if (isDocumentExpiryStatus(value as DocumentRefreshStatusEnum)) {
    return formatStatusValue(
      value,
      DOCUMENT_STATUS_CONFIG,
      DocumentRefreshStatusEnum.RequestSent,
      DocumentRefreshStatusEnum.RequestSentOverdue,
    );
  }

  const mappedStatus = mapDocumentRefreshToCaseStatus(value as DocumentRefreshStatusEnum);
  const { displayText } = mapStatusToText(mappedStatus);
  return displayText;
};

const formatNumericValue = (params: AgGrid.ValueFormatterParams) => {
  const value = Array.isArray(params.value) ? params.value.length : 0;
  return value === 0 ? "None" : value?.toString();
};

const getNumericCellStyle = (params: AgGrid.ValueFormatterParams) => {
  const value = params.value as number;
  return {
    fontStyle: value === 0 ? "italic" : "normal",
    color: value === 0 ? Colors.Neutral500 : "inherit",
    textAlign: "left",
  };
};

const formatDate = (params: AgGrid.ValueFormatterParams<ProfileRowItem>) => {
  if (params.data?.isEntityClientOnboardingProfile) {
    return "--";
  }

  const value = params.value as string;
  if (!value || value === "--") {
    return "--";
  }

  try {
    const date = new Date(value);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

export const profilesColumnDefs: AgGrid.ColDef<ProfileRowItem>[] = [
  {
    field: "name",
    headerName: "Name",
    width: 342,
    cellRenderer: ProfileNameRenderer,
    pinned: "left",
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
    },
    sortable: true,
  },
  {
    field: "domicile",
    headerName: "Domicile",
    minWidth: 200,
    cellRenderer: (params: AGGridCellRenderParams) => <DomicileRenderer params={params} />,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
    },
    sortable: true,
    comparator: sortStrings,
    hide: true,
  },
  {
    field: "role",
    headerName: "Role",
    width: 300,
    cellRenderer: RoleCellRenderer,
    cellClass: roleColumn,
    filter: "agSetColumnFilter",
    filterParams: {
      values: ["investment_profile", "owner", "controller", "signatory", "payment_beneficiary"],
      valueFormatter: (params: AgGrid.ValueFormatterParams) => {
        return formatRoleValue(params.value as string);
      },
    },
    sortable: true,
  },
  {
    field: "amlEntityTypes",
    headerName: "AML Entity Types",
    width: 145,
    valueFormatter: (params: AgGrid.ValueFormatterParams) => {
      const value = params.value as string;
      if (!value || value === "--" || value === "individual") {
        return "Natural Person";
      }
      return capitalizeEachWord(value.replace(/_/g, " "));
    },
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
    },
    sortable: true,
  },
  {
    field: "positions",
    headerName: "Positions",
    width: 120,
    cellRenderer: PositionCellRenderer,
    sortable: false,
    filter: false,
  },
  {
    field: "clients",
    headerName: "Clients",
    width: 120,
    valueFormatter: formatNumericValue,
    cellStyle: getNumericCellStyle,
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      filterOptions: ["equals"],
      maxNumConditions: 1,
      defaultOption: "equals",
    },
  },
  {
    field: "activeCases",
    headerName: "Active cases",
    width: 120,
    cellRenderer: ActiveCasesCellRenderer,
    sortable: false,
    filter: false,
  },
  {
    field: "documentRefreshStatus",
    headerName: "Document status",
    width: 250,
    cellRenderer: DocumentRefreshCellRenderer,
    cellRendererParams: (params: AgGrid.ICellRendererParams<ProfileRowItem>) => ({
      value: params.value as string,
      data: params.data,
    }),
    cellClass: fillCell,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      values: Object.values(DocumentRefreshStatusEnum).filter(
        (status) => status !== DocumentRefreshStatusEnum.NoDocuments,
      ),
      valueFormatter: (params: AgGrid.ValueFormatterParams) => {
        return formatDocumentRefreshStatusValue(params.value as string);
      },
    },
  },
  {
    field: "escalationCaseIds",
    headerName: "Escalations",
    width: 120,
    cellRenderer: EscalationsCellRenderer,
    sortable: false,
    hide: true,
  },
  {
    field: "lastRefresh",
    headerName: "Last refresh",
    width: 120,
    valueFormatter: formatDate,
    filter: "agDateColumnFilter",
    filterParams: {
      maxNumConditions: 1,
      filterOptions: ["equals"],
      defaultOption: "equals",
    },
    sortable: true,
  },
  {
    field: "nextRefresh",
    headerName: "Next refresh",
    width: 120,
    valueFormatter: formatDate,
    filter: "agDateColumnFilter",
    filterParams: {
      maxNumConditions: 1,
      filterOptions: ["equals"],
      defaultOption: "equals",
    },
    sortable: true,
  },
  {
    field: "refreshStatus",
    headerName: "Refresh status",
    width: 293,
    cellRenderer: RefreshStatusCellRenderer,
    cellRendererParams: (params: AgGrid.ICellRendererParams<ProfileRowItem>) => ({
      value: params.value as string,
      data: params.data,
    }),
    sort: "desc",
    cellClass: fillCell,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      values: Object.values(RefreshStatusEnum).filter(
        (status) => status !== RefreshStatusEnum.NotReady,
      ),
      valueFormatter: (params: AgGrid.ValueFormatterParams) => {
        return formatRefreshStatusValue(params.value as string);
      },
    },
  },
];
